import {
  DEFAULT_FEE_ESTIMATE,
  MIN_RELAY_FEE,
  VSIZE_INPUT_P2WPKH,
  VSIZE_OUTPUT_P2WPKH,
  VSIZE_OVERHEAD,
} from '../../constants';
import type { FeePriority } from '../../WaasBitcoinWalletProvider.types';
import { getFeeRecommendations } from '../getFeeRecommendations';

type EstimateTransactionFeeOptions = {
  feePriority?: FeePriority;
  numInputs: number;
  numOutputs: number;
};

/**
 * Estimates transaction fees based on number of inputs and outputs using accurate vSize
 *
 * @param options.feePriority - Priority level for fee estimation ('low', 'medium', or 'high')
 * @param options.numInputs - Number of transaction inputs (UTXOs being spent)
 * @param options.numOutputs - Number of transaction outputs
 * @returns Estimated fee in satoshis
 */
export const estimateTransactionFee = async ({
  feePriority = 'medium',
  numInputs,
  numOutputs,
}: EstimateTransactionFeeOptions): Promise<number> => {
  try {
    const feeData = await getFeeRecommendations();

    let feePerByte: number;

    if (feePriority === 'high') {
      feePerByte = feeData.fastestFee ?? feeData.halfHourFee ?? 1;
    } else if (feePriority === 'low') {
      feePerByte = feeData.economyFee ?? feeData.hourFee ?? 1;
    } else {
      feePerByte =
        feeData.halfHourFee ?? feeData.hourFee ?? feeData.economyFee ?? 1;
    }

    const vSize =
      VSIZE_OVERHEAD +
      numInputs * VSIZE_INPUT_P2WPKH +
      numOutputs * VSIZE_OUTPUT_P2WPKH;

    return Math.ceil(feePerByte * vSize) + MIN_RELAY_FEE;
  } catch {
    return DEFAULT_FEE_ESTIMATE;
  }
};
